import type { Column, Cell } from "@silevis/reactgrid";
import type { SheetRow, SheetColumn } from "../engine/models";
import type { SheetCell } from "../engine/cells";

/**
 * Translates our internal generic cells into actual ReactGrid Cells
 */
export const translateCell = (cell: SheetCell): Cell => {
    switch (cell.type) {
        case "text":
            return {
                type: "text",
                text: cell.text,
                nonEditable: cell.nonEditable,
                style: cell.style as any,
            } as Cell;

        case "number":
            return {
                type: "number",
                value: cell.value,
                nonEditable: cell.nonEditable,
                style: cell.style as any,
            } as Cell;

        case "currency":
            return {
                type: "currency",
                value: cell.value,
                currencySymbol: cell.currencySymbol,
                nonEditable: cell.nonEditable,
                style: cell.style as any,
            } as any;

        case "formula":
            return {
                type: "formula",
                formula: cell.formula,
                value: cell.value,
                nonEditable: cell.nonEditable,
                style: cell.style as any,
            } as any;

        case "chevron":
            return {
                type: "chevron",
                text: cell.text,
                isExpanded: cell.isExpanded,
                hasChildren: cell.hasChildren,
                isDeletable: cell.isDeletable,
                canAddAddon: cell.canAddAddon,
                parentId: cell.parentId,
                indentLevel: cell.indentLevel,
                nonEditable: cell.nonEditable,
                style: cell.style as any,
            } as any;

        case "header":
            return {
                type: "header",
                text: cell.text,
                style: cell.style as any,
            } as any;

        case "iconHeader":
            return {
                type: "iconHeader",
                text: cell.text,
                iconId: (cell as any).iconId,
                nonEditable: true,
                style: cell.style as any,
            } as any;

        case "percentage":
            return {
                type: "percentage",
                value: (cell as any).value ?? 0,
                nonEditable: (cell as any).nonEditable ?? false,
                style: cell.style as any,
            } as any;

        case "dropdown":
            return {
                type: "dropdown",
                value: cell.value,
                options: cell.options,
                variant: cell.variant,
                nonEditable: cell.nonEditable,
                style: cell.style as any,
            } as any;
        case "checkbox":
            return {
                type: "checkbox",
                checked: cell.checked,
                nonEditable: cell.nonEditable,
                style: cell.style as any,
            } as any;
        case "statusDot":
            return {
                type: "statusDot",
                text: cell.text,
                showDot: (cell as any).showDot,
                dotColor: (cell as any).dotColor,
                bgColor: (cell as any).bgColor,
                nonEditable: true,
                style: cell.style as any,
            } as any;
        case "addButton":
            return {
                type: "addButton",
                parentMetadata: cell.parentMetadata,
                rowType: cell.rowType,
                indentLevel: (cell as any).indentLevel,
                style: cell.style as any,
            } as any;

        default:
            return { type: "text", text: "" } as Cell;
    }
};

/**
 * Translates our internal SheetRows to ReactGrid Rows
 */
export const translateRows = (rows: SheetRow[]) => {
    return rows.map((r) => ({
        rowId: r.rowId,
        height: r.height,
        reorderable: r.reorderable,
        cells: r.cells.map((cell) => {
            const translated = translateCell(cell);
            // Inject rowId into chevron cells so action callbacks can identify the source row
            if (cell.type === "chevron") {
                (translated as any).rowId = r.rowId;
            }
            return translated;
        }),
    })) as any[];
};

/**
 * Translates our internal SheetColumns to ReactGrid Columns
 */
export const translateColumns = (cols: SheetColumn[]): Column[] => {
    return cols.map((c) => ({
        columnId: c.columnId,
        width: c.width || 100,
        resizable: c.resizable,
    }));
};
